"use client";

import React from "react";
import { MapPin, Mail, Phone, Clock, Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="19719" className="p-8 bg-accent border border-primary/20 rounded-2xl animate-in fade-in slide-in-from-bottom-4">
        <h3 className="text-xl font-bold text-primary mb-2">Nachricht gesendet</h3>
        <p className="text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-xl">
          Weitere Nachricht senden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name ist erforderlich"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Ihr Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Max Mustermann" {...field} className="bg-card border-border focus-visible:border-primary focus-visible:ring-primary/30 rounded-xl h-12" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-Mail ist erforderlich",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ungültige E-Mail-Adresse"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              E-Mail-Adresse
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="max@beispiel.de" {...field} className="bg-card border-border focus-visible:border-primary focus-visible:ring-primary/30 rounded-xl h-12" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Ungültige Telefonnummer"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Telefonnummer
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+49 30 1234567" {...field} className="bg-card border-border focus-visible:border-primary focus-visible:ring-primary/30 rounded-xl h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Unternehmen
              </FormLabel>
              <FormControl>
                <Input placeholder="Muster GmbH" {...field} className="bg-card border-border focus-visible:border-primary focus-visible:ring-primary/30 rounded-xl h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Nachricht ist erforderlich",
      minLength: {
        value: 20,
        message: "Die Nachricht muss mindestens 20 Zeichen lang sein"
      },
      maxLength: {
        value: 1000,
        message: "Nachricht darf 1000 Zeichen nicht überschreiten"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Ihre Nachricht
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Wie können wir Ihnen bei Ihrer Buchung oder Anfrage helfen?" {...field} className="bg-card border-border focus-visible:border-primary focus-visible:ring-primary/30 rounded-xl min-h-[160px] resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest text-sm rounded-xl shadow-lg shadow-primary/20 transition-all duration-300">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Wird gesendet...
          </> : "Nachricht senden"}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const contactDetails = [{
    icon: MapPin,
    label: "Standort",
    value: "Am alten Rathaus 5a 34253 Lohfelden",
    href: "#"
  }, {
    icon: Phone,
    label: "Telefon",
    value: "056151090993",
    href: "tel:056151090993"
  }, {
    icon: Mail,
    label: "E-Mail",
    value: "buchung@kassel-coworking.de",
    href: "mailto:buchung@kassel-coworking.de"
  }, {
    icon: Clock,
    label: "Öffnungszeiten",
    value: "8-16:30 und nach Vereinbarung. Coworkingspace für Mieter 24/7 offen",
    href: null
  }];
  return <section id="kontaktformular-infos" className="bg-background py-40 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {}
          <div ref={ref} className={`transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="mb-10">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">Schreib uns an.</h2>
              <p className="text-muted-foreground text-lg max-w-md">Hast du Fragen zu unseren Coworking-Arbeitsplätzen oder möchtest du einen Konferenzraum buchen? Wir antworten innerhalb von 24 Stunden.</p>
            </div>

            <FormProvider formKey="contact_form_besichtigungbuchen" formId="contact_form_besichtigungbuchen" sectionName="kontaktformular-infos" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 20,
              maxLength: 1000
            }
          }} submitText="Nachricht senden" successMessage="Vielen Dank! Ihre Nachricht wurde erfolgreich übermittelt.">
              <ContactForm />
            </FormProvider>
          </div>

          {}
          <div className={`flex flex-col gap-8 transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            {}
            <div className="relative w-full aspect-square md:aspect-[4/3] rounded-3xl overflow-hidden bg-muted border border-border shadow-2xl shadow-black/5 group">
              {}
              <div className="absolute inset-0 bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603628/img/map_dark_cyan.png" alt="Unser Standort in Lohfelden" className="w-full h-full object-cover opacity-80 mix-blend-luminosity group-hover:scale-105 transition-transform duration-700" />
                {}
                <div className="absolute inset-0 bg-primary/5 mix-blend-overlay pointer-events-none" />
                <div className="absolute inset-0 bg-background/10 pointer-events-none" />
              </div>

              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                <div className="relative">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full animate-ping" />
                  <MapPin className="size-10 text-primary fill-primary/20 drop-shadow-md" />
                </div>
                <div className="bg-background/90 backdrop-blur-sm px-4 py-1.5 rounded-full border border-border shadow-lg">
                  <span className="text-xs font-mono font-bold text-foreground uppercase tracking-widest">
                    Lohfelden
                  </span>
                </div>
              </div>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 pt-4">
              {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                  <div className="p-3 rounded-xl bg-accent border border-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <item.icon className="size-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                      {item.label}
                    </span>
                    {item.href ? <Link to={item.href} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                        {item.value}
                      </Link> : <span className="text-sm font-medium text-foreground">
                        {item.value}
                      </span>}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}